import { Dispatch, FC, SetStateAction } from "react";
import { FormType } from "../../common";
import { DairyV2OutputDairyCow } from "../../../../models/Baseline/DairyV2Baseline";
import { useFormContext } from "react-hook-form";
import { DairyIntervention } from "../../../../models/Intervention";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { Spacer } from "../../../helpers/Spacer";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { unitLong } from "../../../../utils/unit-utils";
import EditModeButtons from "../../../helpers/EditModeButtons";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";

interface DairyV2OutputCowNewbornCalvesComponentProps {
  formType: FormType;
  fieldItemPrefix: string;
  currentStageData: DairyV2OutputDairyCow;
  isNewbornCalvesEdit: boolean;
  setIsNewbornCalvesEdit: Dispatch<SetStateAction<boolean>>;
  resetEdit: () => void;
}

const DairyV2OutputCowNewbornCalvesComponent: FC<DairyV2OutputCowNewbornCalvesComponentProps> = ({
  formType = FormType.Add,
  fieldItemPrefix,
  currentStageData,
  isNewbornCalvesEdit,
  setIsNewbornCalvesEdit,
  resetEdit,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const fc = useFormContext<DairyIntervention>();

  const userProfile = UserProfilePrefs.getInstance();
  const userUOM = userProfile.getUserUnitPrefs();
  const barnOutputMassUnit = unitLong(userUOM.unitBarnOutputMass as string);

  return (
    <DsmGrid className={classes.dsmGridOneColumnWithBorder}>
      <Spacer />
      {!isNewbornCalvesEdit ? (
        <>
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.weanedCalvesToAnotherStage`)) ?
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.weanedCalvesToAnotherStage`}
                label={intl.formatMessage({
                  id: 'SUSTELL.STAGE.DAIRY.OUTPUT.WEANED_CALVES_TO_ANOTHER_STAGE',
                })}
                tooltip={intl.formatMessage({
                  id: 'SUSTELL.STAGE.DAIRY.OUTPUT.WEANED_CALVES_TO_ANOTHER_STAGE.TOOLTIP',
                })}
                type="number"
                metricUnit={intl.formatMessage({
                  id: 'SUSTELL.STAGE.DAIRY.OUTPUT.WEANED_CALVES_TO_ANOTHER_STAGE.PLACEHOLDER',
                })}
                baseline={currentStageData?.weanedCalvesToAnotherStage}
                disabled
                displayTypeSelect={false}
              />
              <Spacer />
            </div> :
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalWeanedCalvesToAnotherStage`}
              label={intl.formatMessage({
                id: 'SUSTELL.STAGE.DAIRY.OUTPUT.WEANED_CALVES_TO_ANOTHER_STAGE',
              })}
              adornment={intl.formatMessage({
                id: 'SUSTELL.STAGE.DAIRY.OUTPUT.WEANED_CALVES_TO_ANOTHER_STAGE.PLACEHOLDER',
              })}
              tooltip={intl.formatMessage({
                id: 'SUSTELL.STAGE.DAIRY.OUTPUT.WEANED_CALVES_TO_ANOTHER_STAGE.TOOLTIP',
              })}
              type="number"
              defaultValue={currentStageData.weanedCalvesToAnotherStage}
              disabled
            />
          }
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.weanedCalvesSold`)) ?
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.weanedCalvesSold`}
                label={intl.formatMessage({
                  id: 'SUSTELL.STAGE.DAIRY.OUTPUT.WEANED_CALVES_SOLD',
                })}
                type="number"
                metricUnit={intl.formatMessage({
                  id: 'SUSTELL.STAGE.DAIRY.OUTPUT.WEANED_CALVES_SOLD.PLACEHOLDER',
                })}
                baseline={currentStageData?.weanedCalvesSold}
                disabled
                displayTypeSelect={false}
              />
              <Spacer />
            </div> :
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalWeanedCalvesSold`}
              label={intl.formatMessage({
                id: 'SUSTELL.STAGE.DAIRY.OUTPUT.WEANED_CALVES_SOLD',
              })}
              adornment={intl.formatMessage({
                id: 'SUSTELL.STAGE.DAIRY.OUTPUT.WEANED_CALVES_SOLD.PLACEHOLDER',
              })}
              type="number"
              defaultValue={currentStageData.weanedCalvesSold}
              disabled
            />
          }
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.averageWeightAtBirth`)) ?
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.averageWeightAtBirth`}
                label={intl.formatMessage({
                  id: 'SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_BIRTH',
                })}
                tooltip={intl.formatMessage({
                  id: 'SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_BIRTH.TOOLTIP',
                })}
                type="number"
                metricUnit={intl.formatMessage(
                  {
                    id: 'SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_BIRTH.ADORNMENT',
                  },
                  {
                    unit: barnOutputMassUnit
                  }
                )}
                baseline={currentStageData?.averageWeightAtBirth}
                disabled
                displayTypeSelect={false}
              />
              <Spacer />
            </div> :
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalAverageWeightAtBirth`}
              label={intl.formatMessage({
                id: 'SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_BIRTH',
              })}
              adornment={intl.formatMessage(
                {
                  id: 'SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_BIRTH.ADORNMENT',
                },
                {
                  unit: barnOutputMassUnit
                }
              )}
              tooltip={intl.formatMessage({
                id: 'SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_BIRTH.TOOLTIP',
              })}
              type="number"
              defaultValue={currentStageData.averageWeightAtBirth}
              disabled
            />
          }
        </>
      ) : (
        <>
          <RowTextFieldWithMetrics
            name={`${fieldItemPrefix}.weanedCalvesToAnotherStage`}
            label={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.OUTPUT.WEANED_CALVES_TO_ANOTHER_STAGE"
            })}
            tooltip={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.OUTPUT.WEANED_CALVES_TO_ANOTHER_STAGE.TOOLTIP"
            })}
            type="number"
            metricUnit={intl.formatMessage({
              id: 'SUSTELL.STAGE.DAIRY.OUTPUT.WEANED_CALVES_TO_ANOTHER_STAGE.PLACEHOLDER',
            })}
            baseline={currentStageData.weanedCalvesToAnotherStage}
            disabled={false}
          />
          <Spacer />
          <RowTextFieldWithMetrics
            name={`${fieldItemPrefix}.weanedCalvesSold`}
            label={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.OUTPUT.WEANED_CALVES_SOLD"
            })}
            type="number"
            metricUnit={intl.formatMessage({
              id: 'SUSTELL.STAGE.DAIRY.OUTPUT.WEANED_CALVES_SOLD.PLACEHOLDER',
            })}
            baseline={currentStageData.weanedCalvesSold}
            disabled={false}
          />
          <Spacer />
          <RowTextFieldWithMetrics
            name={`${fieldItemPrefix}.averageWeightAtBirth`}
            label={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_BIRTH"
            })}
            tooltip={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_BIRTH.TOOLTIP"
            })}
            type="number"
            metricUnit={intl.formatMessage(
              {
                id: 'SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_BIRTH.ADORNMENT',
              },
              {
                unit: barnOutputMassUnit
              }
            )}
            baseline={currentStageData.averageWeightAtBirth}
            disabled={false}
          />
          <Spacer />
        </>
      )}
      {formType !== FormType.View &&
        <EditModeButtons
          isEditing={isNewbornCalvesEdit}
          onEdit={() => setIsNewbornCalvesEdit(true)}
          onCancel={resetEdit}
        />}
    </DsmGrid>
  )
}

export default DairyV2OutputCowNewbornCalvesComponent;
